import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import { adminApi } from '../../api/admin/adminApi';
import { useAdminTheme } from './ui/useAdminTheme';
import { chartPaletteForTheme, formatConv, formatGrowthLabel, lineChartOptions } from './analytics/analyticsFormat';
import { normalizeOverviewPayload, overviewAttention } from './analytics/overviewSnapshot';
import { activityUserId } from './analytics/countryCentroids';
import { registerAdminCharts } from './analytics/registerAdminCharts';
import styles from './AdminOverviewPage.module.css';

registerAdminCharts();

function formatOverviewUpdated(date) {
  const day = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' }).format(date);
  const time = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(date);
  return `${day} · ${time}`;
}

function titleCase(value) {
  if (!value) return '—';
  return String(value).charAt(0).toUpperCase() + String(value).slice(1);
}

function Kpi({ label, value, hint, tone }) {
  return (
    <article className={styles.kpi}>
      <p className={styles.kpiLabel}>{label}</p>
      <p className={`${styles.kpiValue} ${tone === 'up' ? styles.kpiUp : ''} ${tone === 'down' ? styles.kpiDown : ''}`.trim()}>
        {value}
      </p>
      {hint ? <p className={styles.kpiHint}>{hint}</p> : null}
    </article>
  );
}

function BoardCard({ to, title, children, failed }) {
  return (
    <Link to={to} className={styles.boardCard}>
      <div className={styles.boardHead}>
        <h2 className={styles.boardTitle}>{title}</h2>
        <span className={styles.open}>Open →</span>
      </div>
      {failed ? <p className={styles.mutedFail}>Couldn’t load this snapshot. Open the page to retry.</p> : children}
    </Link>
  );
}

function Metric({ value, label }) {
  return (
    <div className={styles.metric}>
      <span className={styles.metricValue}>{value ?? '—'}</span>
      <span className={styles.metricLabel}>{label}</span>
    </div>
  );
}

function ActivityLink({ item, kind }) {
  const userId = activityUserId(item);
  const email = item.user?.email || item.email || 'Unknown';
  const when = item.loginAt || item.createdAt;
  const label = kind === 'login'
    ? email
    : `${email}${item.isVerified ? ' (Verified)' : ' (Pending)'}`;
  const inner = (
    <>
      <span>{label}</span>
      <span>{when ? new Date(when).toLocaleString() : ''}</span>
    </>
  );
  if (!userId) return <div className={styles.activityItem}>{inner}</div>;
  return (
    <Link to={`/admin/users?user=${encodeURIComponent(userId)}`} className={styles.activityItem}>
      {inner}
    </Link>
  );
}

export default function AdminOverviewPage() {
  const { registerShellMeta, setRefreshHandler } = useOutletContext() || {};
  const { theme } = useAdminTheme();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const snapshotRef = useRef(null);
  snapshotRef.current = snapshot;

  const load = useCallback(async () => {
    setError('');
    if (!snapshotRef.current) setLoading(true);
    try {
      const result = await adminApi.getOverview();
      const next = normalizeOverviewPayload(result.data || result);
      setSnapshot(next);
      setLastUpdatedAt(new Date());
      if (!next.users?.total && next.errors.analytics) {
        setError('Failed to load platform metrics.');
      }
    } catch (err) {
      setError('Failed to load admin overview.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    registerShellMeta?.({
      title: 'Overview',
      subtitle: 'Live snapshot across the admin platform',
      lastUpdatedAt,
    });
  }, [lastUpdatedAt, registerShellMeta]);

  useEffect(() => {
    setRefreshHandler?.(() => load);
  }, [setRefreshHandler, load]);

  const palette = useMemo(() => chartPaletteForTheme(theme), [theme]);
  const sparkData = useMemo(() => {
    const daily = snapshot?.daily || [];
    return {
      labels: daily.map((item) => formatGrowthLabel(item._id)),
      datasets: [{
        label: 'New users',
        data: daily.map((item) => item.count || 0),
        borderColor: palette.line,
        backgroundColor: palette.lineFill,
        tension: 0.25,
        fill: true,
        pointRadius: 0,
      }],
    };
  }, [snapshot, palette]);

  if (loading && !snapshot) {
    return <div className={styles.loading}>Loading platform overview…</div>;
  }

  if ((error && !snapshot) || !snapshot) {
    return (
      <div className={styles.error}>
        <p>{error || 'No overview data available'}</p>
        <button type="button" className={`btn ${styles.retry}`} onClick={load}>Retry</button>
      </div>
    );
  }

  const growthTone = snapshot.growth.rate > 0 ? 'up' : snapshot.growth.rate < 0 ? 'down' : undefined;
  const attention = overviewAttention(snapshot);
  const gf = snapshot.gf;
  const logins = snapshot.recentActivity?.recentLogins || [];
  const registrations = snapshot.recentActivity?.recentRegistrations || [];

  return (
    <div className={styles.page}>
      <div className={styles.metaRow}>
        {attention.length ? (
          <div className={styles.attention} aria-label="Items needing attention">
            {attention.map((item) => (
              <Link key={item.id} to={item.to} className={styles.attentionChip}>
                {item.label}
              </Link>
            ))}
          </div>
        ) : null}
        {lastUpdatedAt ? (
          <p className={styles.updated} role="status">
            <span className={styles.updatedDot} aria-hidden="true" />
            <span className={styles.updatedLabel}>Updated</span>
            <time dateTime={lastUpdatedAt.toISOString()}>{formatOverviewUpdated(lastUpdatedAt)}</time>
          </p>
        ) : null}
      </div>

      <section className={styles.kpiGrid} aria-label="Platform metrics">
        <Kpi label="Total users" value={snapshot.users.total ?? '—'} hint="Registered accounts" />
        <Kpi
          label="Growth"
          value={snapshot.growth.label}
          hint={snapshot.growth.isNew
            ? 'This week · no signups in the week before'
            : 'New users vs prior 7 days'}
          tone={growthTone}
        />
        <Kpi label="New today" value={snapshot.users.newToday ?? 0} hint="Registrations" />
        <Kpi label="Active today" value={snapshot.users.activeToday ?? 0} hint="Daily active users" />
        <Kpi label="Verified" value={snapshot.verificationLabel} hint="Verification rate" />
        <Kpi label="Online now" value={snapshot.sessions.onlineNow ?? 0} hint="Active sessions" />
      </section>

      <section className={styles.sparkCard}>
        <div className={styles.sparkHead}>
          <div>
            <h2 className={styles.sparkTitle}>User growth</h2>
            <p className={styles.note}>
              {snapshot.users.newThisWeek ?? 0} new this week · {snapshot.users.newThisMonth ?? 0} this month
              {gf?.guestToUserConversion?.rate != null ? ` · Guest → user ${formatConv(gf.guestToUserConversion.rate)}` : ''}
            </p>
          </div>
          <Link to="/admin/growth" className={styles.open}>Open Growth →</Link>
        </div>
        {snapshot.errors.analytics ? (
          <p className={styles.mutedFail}>Growth series unavailable.</p>
        ) : (
          <div className={styles.sparkChart}>
            <Line
              data={sparkData}
              options={{
                ...lineChartOptions(palette),
                plugins: { legend: { display: false } },
              }}
            />
          </div>
        )}
      </section>

      {(logins.length || registrations.length) ? (
        <section className={styles.activity} aria-label="Recent activity">
          <div className={styles.sparkHead}>
            <h2 className={styles.sparkTitle}>Recent activity</h2>
            <Link to="/admin/users" className={styles.open}>Open Users →</Link>
          </div>
          <div className={styles.activityGrid}>
            {logins.length ? (
              <div>
                <h3 className={styles.activityTitle}>Logins</h3>
                {logins.slice(0, 5).map((login, index) => (
                  <ActivityLink key={`${activityUserId(login) || login.user?.email || 'login'}-${login.loginAt || index}`} item={login} kind="login" />
                ))}
              </div>
            ) : null}
            {registrations.length ? (
              <div>
                <h3 className={styles.activityTitle}>Registrations</h3>
                {registrations.slice(0, 5).map((user, index) => (
                  <ActivityLink key={`${activityUserId(user) || user.email || 'user'}-${user.createdAt || index}`} item={user} kind="registration" />
                ))}
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      <section className={styles.board} aria-label="Admin areas">
        <BoardCard to="/admin/users" title="Users" failed={snapshot.errors.analytics}>
          <div className={styles.metrics}>
            <Metric value={snapshot.users.total ?? '—'} label="Total" />
            <Metric value={snapshot.sessions.todayCount ?? snapshot.sessions.sessionsToday ?? 0} label="Sessions today" />
            <Metric value={`${snapshot.sessions.avgDurationMinutes ?? 0}m`} label="Avg duration" />
          </div>
        </BoardCard>

        <BoardCard to="/admin/growth" title="Growth" failed={snapshot.errors.analytics}>
          <div className={styles.metrics}>
            <Metric value={snapshot.growth.label} label="7-day growth" />
            <Metric value={titleCase(gf?.funnelHealth?.level)} label="Funnel health" />
            <Metric value={formatConv(gf?.guestToUserConversion?.rate)} label="Guest → user" />
          </div>
        </BoardCard>

        <BoardCard to="/admin/enterprise" title="Enterprise" failed={snapshot.errors.enterprise}>
          <div className={styles.metrics}>
            <Metric value={snapshot.enterprises.count} label="Companies" />
            <Metric value={snapshot.enterprises.billingAttention} label="Billing attention" />
            <Metric value={snapshot.enterprises.newEnquiries ?? '—'} label="New enquiries" />
          </div>
        </BoardCard>

        <BoardCard to="/admin/orders" title="Orders" failed={snapshot.errors.orders}>
          {snapshot.orders.configured === false ? (
            <p className={styles.note}>Shopify is not configured in this environment.</p>
          ) : (
            <div className={styles.metrics}>
              <Metric value={snapshot.orders.today ?? '—'} label="Orders today" />
              <Metric value={snapshot.orders.revenueToday ?? '—'} label="Revenue today" />
              <Metric value={snapshot.orders.revenue7d ?? '—'} label="7-day revenue" />
            </div>
          )}
        </BoardCard>

        <BoardCard to="/admin/cards" title="Cards" failed={snapshot.errors.cards}>
          <div className={styles.metrics}>
            <Metric value={snapshot.cards?.shopifyStock ?? '—'} label="Shopify stock" />
            <Metric value={snapshot.cards?.reserved ?? '—'} label="Activation pending" />
            <Metric value={snapshot.cards?.active ?? '—'} label="Linked" />
          </div>
        </BoardCard>

        <BoardCard to="/admin/fulfilment" title="Fulfilment" failed={snapshot.errors.fulfilment}>
          <div className={styles.metrics}>
            <Metric value={snapshot.fulfilment?.readyToProgramme ?? '—'} label="Ready to programme" />
            <Metric value={snapshot.fulfilment?.needsProgramming ?? '—'} label="Needs programming" />
            <Metric value={snapshot.fulfilment?.flaggedForReview ?? '—'} label="Flagged" />
          </div>
        </BoardCard>

        <BoardCard to="/admin/campaigns" title="Campaigns" failed={snapshot.errors.campaigns}>
          <div className={styles.metrics}>
            <Metric value={snapshot.campaigns.count} label="Campaigns" />
            <Metric value={snapshot.campaigns.issued} label="Cards issued" />
            <Metric
              value={snapshot.campaigns.activation == null ? '—' : `${snapshot.campaigns.activation}%`}
              label="Avg activation"
            />
          </div>
        </BoardCard>

        <BoardCard to="/admin/applications" title="Applications" failed={snapshot.errors.applications}>
          <div className={styles.metrics}>
            <Metric value={snapshot.applications.total ?? '—'} label="Total" />
            <Metric value={snapshot.applications.neu ?? '—'} label="New" />
          </div>
        </BoardCard>

        <BoardCard to="/admin/security" title="Security" failed={snapshot.errors.security}>
          <div className={styles.metrics}>
            <Metric
              value={snapshot.security?.score != null ? snapshot.security.score.toFixed(1) : '—'}
              label="Score /10"
            />
            <Metric value={snapshot.security?.critical ?? '—'} label="Critical" />
            <Metric value={snapshot.security?.blocked ?? '—'} label="Blocked IPs" />
          </div>
        </BoardCard>
      </section>
    </div>
  );
}
